import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { tasksAPI, projectsAPI, teamAPI, serviceDeliveryAPI, timeTrackingAPI } from '../lib/api';
import { CeoTasksByPerson, PersonTaskSections } from './CeoTasksByPerson';
import { ArchivedTasksDialog } from './ArchivedTasksDialog';
import { Archive } from 'lucide-react';
import { useNotificationStore, useAuthStore } from '../store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useTimerStore, elapsedSeconds } from '../lib/timerStore';
import {
  CheckCircle2,
  Circle,
  Plus,
  Search,
  Calendar,
  Clock,
  AlertCircle,
  Loader2,
  Filter,
  Trash2,
  Play,
  Pause,
  Users,
  Building,
  MessageSquare,
  Paperclip,
  Send,
  X,
  Upload,
  FileText,
  Image,
  File,
  ChevronRight,
  Timer,
  Eye,
  UserPlus,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const priorityConfig = {
  high: { label: 'Alta', color: 'bg-red-100 text-red-700 border-red-200' },
  medium: { label: 'Media', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { label: 'Baja', color: 'bg-green-100 text-green-700 border-green-200' },
};

const statusConfig = {
  pending: { label: 'Pendiente', color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'En progreso', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completada', color: 'bg-emerald-100 text-emerald-700' },
};

// Datos de ejemplo con comentarios y archivos
// Función para formatear tiempo
const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

// Componente de detalle de tarea
function TaskDetailModal({ task, team, projects, onClose, onUpdate, onReload, addNotification, canSeeTimeTotals = true }) {
  const [editedTask, setEditedTask] = useState(task);
  const [newComment, setNewComment] = useState('');
  const { active: activeTimer, offsetMs, busy: savingTime, start: timerStart, stop: timerStop } = useTimerStore();
  const isTracking = activeTimer?.task_id === task.id;
  const [, setTick] = useState(0);
  const segmentSec = isTracking ? elapsedSeconds(activeTimer, offsetMs) : 0;
  const displaySeconds = (task.total_time_minutes || 0) * 60 + segmentSec;
  const fileInputRef = useRef(null);
  const [timeMethod, setTimeMethod] = useState('task');

  useEffect(() => {
    timeTrackingAPI.getConfig().then((c) => setTimeMethod(c.method || 'task')).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isTracking) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isTracking]);

  const handleToggleTimer = async () => {
    if (isTracking) {
      await timerStop();
      onReload && onReload();
      return;
    }
    if (!editedTask.project_id) {
      toast.error('Elige el cliente de esta tarea (campo "Proyecto/Cliente") antes de iniciar el cronómetro');
      return;
    }
    if (editedTask.project_id !== task.project_id) {
      await tasksAPI.update(task.id, { project_id: editedTask.project_id }).catch(() => {});
    }
    if (await timerStart(task.id)) onReload && onReload();
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    
    const comment = {
      id: `c${Date.now()}`,
      autor: 'Carlos Méndez', // Usuario actual
      texto: newComment,
      fecha: new Date().toISOString(),
      avatar: 'CM',
    };
    
    setEditedTask({
      ...editedTask,
      comentarios: [...(editedTask.comentarios || []), comment],
    });
    
    // Generar notificación para el asignado
    if (editedTask.assigned_to) {
      const assignee = team.find(m => m.id === editedTask.assigned_to);
      if (assignee) {
        addNotification({
          tipo: 'comentario',
          titulo: 'Nuevo comentario en tu tarea',
          mensaje: `Carlos Méndez comentó en "${editedTask.title}"`,
          link: '/tasks',
          avatar: 'CM',
        });
      }
    }
    
    setNewComment('');
    toast.success('Comentario añadido');
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: `f${Date.now()}_${Math.random()}`,
      nombre: file.name,
      tipo: file.type.startsWith('image/') ? 'image' : file.type === 'application/pdf' ? 'pdf' : 'file',
      tamaño: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
      fecha: new Date().toISOString().split('T')[0],
    }));
    
    setEditedTask({
      ...editedTask,
      archivos: [...(editedTask.archivos || []), ...newFiles],
    });
    toast.success(`${files.length} archivo(s) añadido(s)`);
  };

  const handleRemoveFile = (fileId) => {
    setEditedTask({
      ...editedTask,
      archivos: editedTask.archivos.filter(f => f.id !== fileId),
    });
  };

  const handleSave = () => {
    // Verificar si se cambió la asignación
    if (editedTask.assigned_to !== task.assigned_to && editedTask.assigned_to) {
      const newAssignee = team.find(m => m.id === editedTask.assigned_to);
      if (newAssignee) {
        addNotification({
          tipo: 'asignacion',
          titulo: 'Nueva tarea asignada',
          mensaje: `Te han asignado la tarea "${editedTask.title}"`,
          link: '/tasks',
          avatar: 'CM', // Usuario actual
        });
      }
    }
    
    // Verificar si se completó la tarea
    if (editedTask.status === 'completed' && task.status !== 'completed') {
      addNotification({
        tipo: 'completada',
        titulo: 'Tarea completada',
        mensaje: `La tarea "${editedTask.title}" ha sido completada`,
        link: '/tasks',
        avatar: 'CM',
      });
    }
    
    onUpdate({ ...editedTask });
    toast.success('Tarea actualizada');
    onClose();
  };

  const getFileIcon = (tipo) => {
    switch (tipo) {
      case 'pdf': return <FileText className="w-4 h-4 text-red-500" />;
      case 'image': return <Image className="w-4 h-4 text-blue-500" />;
      default: return <File className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Checkbox
            checked={editedTask.status === 'completed'}
            onCheckedChange={(checked) => 
              setEditedTask({ ...editedTask, status: checked ? 'completed' : 'pending' })
            }
          />
          <Input
            value={editedTask.title}
            onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
            className="text-lg font-semibold border-none p-0 h-auto focus-visible:ring-0"
          />
        </DialogTitle>
      </DialogHeader>

      {task?.assigned_by_name && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground -mt-2" data-testid="detail-assigned-by">
          <UserPlus className="w-3.5 h-3.5" />
          Asignada por: <span className="font-medium text-foreground">{task.assigned_by_name}</span>
        </div>
      )}

      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-6 pb-4">
          {/* Descripción */}
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea
              value={editedTask.description}
              onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
              placeholder="Añade una descripción..."
              rows={3}
            />
          </div>

          {/* Detalles en grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select
                value={editedTask.status}
                onValueChange={(value) => setEditedTask({ ...editedTask, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="in_progress">En progreso</SelectItem>
                  <SelectItem value="completed">Completada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={editedTask.priority}
                onValueChange={(value) => setEditedTask({ ...editedTask, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="low">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fecha Límite</Label>
              <Input
                type="date"
                value={editedTask.due_date}
                onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Tiempo Estimado</Label>
              <Input
                value={editedTask.tiempo_estimado}
                onChange={(e) => setEditedTask({ ...editedTask, tiempo_estimado: e.target.value })}
                placeholder="Ej: 2h 30m"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Asignado a</Label>
              <Select
                value={editedTask.assigned_to || 'none'}
                onValueChange={(value) => setEditedTask({ ...editedTask, assigned_to: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {team.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <span className="flex items-center gap-2">
                        <Avatar className="w-5 h-5">
                          <AvatarFallback className="text-xs">{member.avatar}</AvatarFallback>
                        </Avatar>
                        {member.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Proyecto / Cliente</Label>
              <Select
                value={editedTask.project_id || 'none'}
                onValueChange={(value) => setEditedTask({ ...editedTask, project_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin proyecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin proyecto</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Registro de Tiempo (solo método por tarea) */}
          {timeMethod !== 'jornada' && (
          <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-blue-700">{canSeeTimeTotals ? 'Tiempo Registrado' : 'Cronómetro'}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Timer className="w-5 h-5 text-blue-600" />
                  {canSeeTimeTotals ? (
                    <>
                      <span className="text-2xl font-bold text-blue-700" data-testid="task-timer-display">{formatTime(displaySeconds)}</span>
                      {editedTask.tiempo_estimado && (
                        <span className="text-sm text-blue-600">/ {editedTask.tiempo_estimado} estimado</span>
                      )}
                    </>
                  ) : (
                    <span className="text-sm font-medium text-blue-700 inline-flex items-center gap-1.5" data-testid="task-timer-status">
                      {isTracking && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                      {isTracking ? 'En marcha · se está registrando tu tiempo' : 'Sin cronómetro activo'}
                    </span>
                  )}
                </div>
              </div>
              <Button
                onClick={handleToggleTimer}
                disabled={savingTime}
                data-testid="task-timer-toggle"
                className={isTracking ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'}
              >
                {isTracking ? (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Parar
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Iniciar
                  </>
                )}
              </Button>
            </div>
          </div>
          )}

          {/* Archivos Adjuntos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Archivos Adjuntos ({editedTask.archivos?.length || 0})
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Subir Archivo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
            
            {editedTask.archivos?.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {editedTask.archivos.map((archivo) => (
                  <div
                    key={archivo.id}
                    className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30 group"
                  >
                    {getFileIcon(archivo.tipo)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{archivo.nombre}</p>
                      <p className="text-xs text-muted-foreground">{archivo.tamaño}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={() => handleRemoveFile(archivo.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
                No hay archivos adjuntos
              </div>
            )}
          </div>

          {/* Comentarios */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Comentarios ({editedTask.comentarios?.length || 0})
            </Label>
            
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {editedTask.comentarios?.length > 0 ? (
                editedTask.comentarios.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-muted/30">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {comment.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{comment.autor}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.fecha).toLocaleString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="text-sm mt-1">{comment.texto}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No hay comentarios aún
                </div>
              )}
            </div>

            {/* Añadir comentario */}
            <div className="flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Escribe un comentario..."
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
              />
              <Button onClick={handleAddComment} size="icon">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Footer con acciones */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleSave} className="gradient-primary text-white">
          Guardar Cambios
        </Button>
      </div>
    </DialogContent>
  );
}

export function TasksPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const isMember = currentUser?.role === 'member';
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [activeTab, setActiveTab] = useState('mis-tareas');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const { active: activeTimer, offsetMs: timerOffset, start: timerStart, stop: timerStop } = useTimerStore();
  const trackingTaskId = activeTimer?.task_id || null;
  const [nowMs, setNowMs] = useState(Date.now());
  const [clientPickTask, setClientPickTask] = useState(null);
  const [clientPickValue, setClientPickValue] = useState('');

  // Tick por segundo mientras hay un cronómetro activo (para ver el tiempo avanzar en vivo)
  useEffect(() => {
    if (!trackingTaskId) return;
    const iv = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [trackingTaskId]);

  const stopQuickTimer = async () => {
    await timerStop();
    await loadData();
  };

  const { addNotification } = useNotificationStore();
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    project_id: '',
    assigned_to: '',
    due_date: '',
    tiempo_estimado: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  // Deep-link desde el calendario de Eventos: /tasks?task=<id> abre esa tarea
  // Deep-link desde el Dashboard CEO: /tasks?view=mine|team|clients abre esa pestaña
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedRef = useRef(false);
  const viewParam = searchParams.get('view');
  const clientParam = searchParams.get('client');
  const [archivedOpen, setArchivedOpen] = useState(false);
  const initialCeoTab = viewParam === 'mine' ? currentUser?.id : viewParam === 'team' ? 'team' : viewParam === 'clients' ? 'clients' : null;
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (!taskId || deepLinkedRef.current || tasks.length === 0) return;
    const match = tasks.find((t) => t.id === taskId);
    if (match) {
      setSelectedTask(match);
      deepLinkedRef.current = true;
      setSearchParams({}, { replace: true });
    }
  }, [tasks, searchParams, setSearchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasksData, teamData, clientsData] = await Promise.all([
        tasksAPI.getAll().catch(() => []),
        teamAPI.getAll().catch(() => []),
        serviceDeliveryAPI.getClients().catch(() => []),
      ]);
      
      // Map tasks to expected format
      const mappedTasks = (tasksData || []).map(t => ({
        ...t,
        tiempo_registrado: t.time_logged || 0,
        tiempo_estimado: t.estimated_time || '',
        comentarios: t.comments || [],
        archivos: t.attachments || [],
      }));
      
      // Map team members to expected format
      const mappedTeam = (teamData || []).map(m => ({
        id: m.id,
        name: m.name || m.username,
        role: m.role,
        avatar: (m.name || m.username || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
      }));
      
      // Project/Client dropdown = real clients from CRM / Service Delivery (live)
      const clientList = Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []);
      const mappedProjects = clientList.map(c => ({
        id: c._id || c.id,
        name: c.nombre || c.name || 'Sin nombre',
      }));
      
      setTasks(mappedTasks);
      setTeamMembers(mappedTeam);
      setProjects(mappedProjects);
    } catch (error) {
      console.error('Error loading tasks data:', error);
      toast.error('Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    try {
      const taskData = {
        title: newTask.title,
        description: newTask.description,
        priority: newTask.priority,
        status: newTask.status,
        project_id: newTask.project_id || null,
        assigned_to: newTask.assigned_to || (isMember ? currentUser?.id : null),
        due_date: newTask.due_date || null,
        estimated_time: newTask.tiempo_estimado,
      };

      await tasksAPI.create(taskData);
      await loadData();
      
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        status: 'pending',
        project_id: '',
        assigned_to: '',
        due_date: '',
        tiempo_estimado: '',
      });
      setIsAddingTask(false);
      toast.success('Tarea creada correctamente');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Error al crear la tarea');
    }
  };

  const handleUpdateTask = async (updatedTask) => {
    try {
      await tasksAPI.update(updatedTask.id, {
        title: updatedTask.title,
        description: updatedTask.description,
        priority: updatedTask.priority,
        status: updatedTask.status,
        project_id: updatedTask.project_id,
        assigned_to: updatedTask.assigned_to,
        due_date: updatedTask.due_date,
        estimated_time: updatedTask.tiempo_estimado,
        time_logged: updatedTask.tiempo_registrado,
      });
      await loadData();
      toast.success('Tarea actualizada');
    } catch (error) {
      console.error('Error updating task:', error);
      // Update locally as fallback
      setTasks(tasks.map(t => t.id === updatedTask.id ? updatedTask : t));
    }
  };

  const handleToggleComplete = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    
    try {
      await tasksAPI.update(taskId, { status: newStatus });
      await loadData();
      toast.success(newStatus === 'completed' ? 'Tarea completada' : 'Tarea reabierta');
    } catch (error) {
      // Update locally as fallback
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast.success(newStatus === 'completed' ? 'Tarea completada' : 'Tarea reabierta');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksAPI.delete(taskId);
      await loadData();
      toast.success('Tarea eliminada');
    } catch (error) {
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Tarea eliminada');
    }
  };

  const toggleQuickTimer = async (taskId) => {
    if (trackingTaskId === taskId) {
      await stopQuickTimer();
    } else {
      const task = tasks.find((t) => t.id === taskId);
      if (task && !task.project_id) {
        setClientPickValue('');
        setClientPickTask(task);
        return;
      }
      startQuickTimer(taskId);
    }
  };

  const startQuickTimer = async (taskId) => {
    if (await timerStart(taskId)) loadData();
  };

  const confirmClientPick = async () => {
    if (!clientPickValue) { toast.error('Elige el cliente para el que trabajas'); return; }
    try {
      await tasksAPI.update(clientPickTask.id, { project_id: clientPickValue });
      setTasks((prev) => prev.map((t) => (t.id === clientPickTask.id ? { ...t, project_id: clientPickValue } : t)));
      startQuickTimer(clientPickTask.id);
      setClientPickTask(null);
    } catch (e) {
      toast.error('No se pudo asignar el cliente');
    }
  };

  // Workflow tasks (auto-generated from content) deep-link to the publication
  const openTaskTarget = (task) => {
    if (task.content_id && task.content_client_id) {
      navigate('/service-delivery', {
        state: {
          openClientId: task.content_client_id,
          openPostId: task.content_id,
          openSection: 'calendario',
        },
      });
      return;
    }
    if (task.task_type === 'accion_semanal' && task.ref_client_id) {
      navigate('/service-delivery', {
        state: {
          openClientId: task.ref_client_id,
          openSection: 'acciones',
        },
      });
      return;
    }
    if (task.task_type === 'subtarea_accion' && task.ref_client_id) {
      navigate('/service-delivery', {
        state: {
          openClientId: task.ref_client_id,
          openSection: 'acciones',
        },
      });
      return;
    }
    setSelectedTask(task);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesProject = filterProject === 'all'
      || (filterProject === 'none' ? !task.project_id : task.project_id === filterProject);
    return matchesSearch && matchesStatus && matchesProject;
  });

  const pendingTasks = tasks.filter(t => t.status !== 'completed');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const highPriorityTasks = tasks.filter(t => t.priority === 'high' && t.status !== 'completed');

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || '';
  };

  const getAssigneeName = (userId) => {
    const user = teamMembers.find(u => u.id === userId);
    return user?.name || 'Sin asignar';
  };

  const getAssigneeAvatar = (userId) => {
    const user = teamMembers.find(u => u.id === userId);
    return user?.avatar || '?';
  };

  // Equipo primero (member/manager), luego CEOs; alfabético dentro de cada grupo
  const peopleOrder = useMemo(() => {
    const rank = (m) => (m.role === 'ceo' ? 1 : 0);
    return [...teamMembers].sort((a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '', 'es'));
  }, [teamMembers]);

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'completed') return false;
    const d = new Date(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const renderTaskRow = (task) => (
    <motion.div
      key={task.id}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer group ${task.priority === 'high' && task.status !== 'completed' ? 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-950/10' : ''}`}
      data-testid={`task-item-${task.id}`}
    >
      <Checkbox
        checked={task.status === 'completed'}
        onCheckedChange={() => handleToggleComplete(task.id)}
        className="data-[state=checked]:bg-emerald-500"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div 
        className="flex-1 min-w-0"
        onClick={() => openTaskTarget(task)}
      >
        <div className="flex items-center gap-2">
          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </p>
          {task.content_id && (
            <Badge variant="outline" className="text-[10px] border-primary/40 text-primary gap-1">
              <ChevronRight className="w-3 h-3" /> Publicación
            </Badge>
          )}
          {task.comentarios?.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MessageSquare className="w-3 h-3" />
              {task.comentarios.length}
            </span>
          )}
          {task.archivos?.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Paperclip className="w-3 h-3" />
              {task.archivos.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
          {task.assigned_to && (
            <span className="flex items-center gap-1">
              <Avatar className="w-4 h-4">
                <AvatarFallback className="text-[8px]">
                  {getAssigneeAvatar(task.assigned_to)}
                </AvatarFallback>
              </Avatar>
              {getAssigneeName(task.assigned_to)}
            </span>
          )}
          {task.project_id && (
            <span className="flex items-center gap-1">
              <Building className="w-3 h-3" />
              {getProjectName(task.project_id)}
            </span>
          )}
          {task.due_date && (
            <span className={`flex items-center gap-1 ${isOverdue(task) ? 'text-red-600 font-semibold' : ''}`} data-testid={`task-due-${task.id}`}>
              <Calendar className="w-3 h-3" />
              {new Date(task.due_date).toLocaleDateString('es-ES')}{isOverdue(task) ? ' · Vencida' : ''}
            </span>
          )}
          {task.created_at && (
            <span className="flex items-center gap-1" data-testid={`task-created-at-${task.id}`}>
              <Clock className="w-3 h-3" />
              Creada: {new Date(task.created_at).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {task.assigned_by_name && (
            <span className="flex items-center gap-1" data-testid={`task-assigned-by-${task.id}`}>
              <UserPlus className="w-3 h-3" />
              Asignada por: {task.assigned_by_name}
            </span>
          )}
          {(() => {
            const isRunning = trackingTaskId === task.id;
            const liveSec = isRunning && nowMs ? elapsedSeconds(activeTimer, timerOffset) : 0;
            const totalSec = (task.total_time_minutes || 0) * 60 + liveSec;
            if (isMember) {
              // Miembros: solo indicador de cronómetro activo, nunca totales
              if (!isRunning) return null;
              return (
                <span className="flex items-center gap-1 font-medium text-red-600" data-testid={`task-time-${task.id}`} title="Cronómetro en marcha">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <Timer className="w-3 h-3" /> En marcha
                </span>
              );
            }
            if (!isRunning && totalSec === 0) return null;
            return (
              <span
                className={`flex items-center gap-1 font-medium ${isRunning ? 'text-red-600' : 'text-blue-600'}`}
                data-testid={`task-time-${task.id}`}
                title={isRunning ? 'Cronómetro en marcha' : 'Tiempo total acumulado en esta tarea'}
              >
                {isRunning && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                <Timer className="w-3 h-3" />
                {formatTime(totalSec)}
              </span>
            );
          })()}
        </div>
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          toggleQuickTimer(task.id);
        }}
        title={trackingTaskId === task.id ? 'Parar y registrar el tiempo' : 'Iniciar cronómetro en esta tarea'}
        data-testid={`quick-timer-${task.id}`}
        className={trackingTaskId === task.id ? 'text-red-500 bg-red-50' : 'text-muted-foreground'}
      >
        {trackingTaskId === task.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>

      <Badge className={priorityConfig[task.priority]?.color}>
        {priorityConfig[task.priority]?.label}
      </Badge>
      
      <Badge className={statusConfig[task.status]?.color}>
        {statusConfig[task.status]?.label}
      </Badge>

      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          openTaskTarget(task);
        }}
        className="opacity-0 group-hover:opacity-100"
      >
        <Eye className="w-4 h-4" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteTask(task.id);
        }}
        className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </motion.div>
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="tasks-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mis Tareas</h1>
          <p className="text-muted-foreground mt-1">
            {isMember
              ? 'Gestiona tus tareas con tiempo, comentarios y archivos'
              : 'Vista de conjunto del equipo · las mismas tareas que ve cada persona en su "Mis Tareas"'}
          </p>
        </div>
        <Dialog open={isAddingTask} onOpenChange={(open) => {
          setIsAddingTask(open);
          if (open) {
            // Refresca la lista de clientes al abrir para reflejar altas recientes
            serviceDeliveryAPI.getClients()
              .then((d) => {
                const list = Array.isArray(d) ? d : (d?.clients || []);
                setProjects(list.map(c => ({ id: c._id || c.id, name: c.nombre || c.name || 'Sin nombre' })));
              })
              .catch(() => {});
          }
        }}>
          <Button variant="outline" onClick={() => setArchivedOpen(true)} data-testid="archived-tasks-btn" title="Completadas hace más de 7 días (histórico)">
            <Archive className="w-4 h-4 mr-2" /> Archivadas
          </Button>
          <ArchivedTasksDialog open={archivedOpen} onClose={() => setArchivedOpen(false)} onOpenTask={(t) => { setArchivedOpen(false); setSelectedTask(t); }} />
          <DialogTrigger asChild>
            <Button className="gradient-primary text-white" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tarea
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nueva Tarea</DialogTitle>
              <DialogDescription>Crea una nueva tarea con todos los detalles</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="¿Qué necesitas hacer?"
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Detalles adicionales sobre la tarea..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Select
                    value={newTask.priority}
                    onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="low">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select
                    value={newTask.status}
                    onValueChange={(value) => setNewTask({ ...newTask, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="in_progress">En progreso</SelectItem>
                      <SelectItem value="completed">Completada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Límite</Label>
                  <Input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tiempo Estimado</Label>
                  <Input
                    value={newTask.tiempo_estimado}
                    onChange={(e) => setNewTask({ ...newTask, tiempo_estimado: e.target.value })}
                    placeholder="Ej: 2h 30m"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Asignar a</Label>
                  <Select
                    value={newTask.assigned_to}
                    onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Proyecto / Cliente</Label>
                  <Select
                    value={newTask.project_id}
                    onValueChange={(value) => setNewTask({ ...newTask, project_id: value })}
                  >
                    <SelectTrigger data-testid="task-project-select">
                      <SelectValue placeholder="Seleccionar cliente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsAddingTask(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleAddTask} data-testid="save-task-btn">
                  Crear Tarea
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{tasks.length}</p>
              </div>
              <Circle className="w-8 h-8 text-gray-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pendientes</p>
                <p className="text-2xl font-bold">{pendingTasks.length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Completadas <span className="opacity-70">(últimos 7 días)</span></p>
                <p className="text-2xl font-bold">{completedTasks.length}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Alta Prioridad</p>
                <p className="text-2xl font-bold">{highPriorityTasks.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar tareas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-tasks-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="in_progress">En progreso</SelectItem>
            <SelectItem value="completed">Completadas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-[200px]" data-testid="filter-client-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar por cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los clientes</SelectItem>
            <SelectItem value="none">Sin cliente</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </motion.div>

      {/* Tasks List */}
      <motion.div variants={itemVariants}>
        {isMember ? (
          <Card>
            <CardContent className="p-0">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">No hay tareas que mostrar</p>
                </div>
              ) : (
                <PersonTaskSections tasks={filteredTasks} renderRow={renderTaskRow} testKey="member" initialClient={clientParam} />
              )}
            </CardContent>
          </Card>
        ) : (
          <CeoTasksByPerson tasks={filteredTasks} people={peopleOrder} renderRow={renderTaskRow} initialTab={initialCeoTab} initialClient={clientParam} />
        )}
      </motion.div>

      {/* Task Detail Modal */}
      <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        {selectedTask && (
          <TaskDetailModal canSeeTimeTotals={!isMember}
            task={selectedTask}
            team={teamMembers}
            projects={projects}
            onClose={() => setSelectedTask(null)}
            onUpdate={handleUpdateTask}
            onReload={loadData}
            addNotification={addNotification}
          />
        )}
      </Dialog>

      {/* Elegir cliente obligatorio antes de arrancar el cronómetro */}
      <Dialog open={!!clientPickTask} onOpenChange={(open) => !open && setClientPickTask(null)}>
        <DialogContent className="max-w-sm" data-testid="client-pick-dialog">
          <DialogHeader>
            <DialogTitle>¿Para qué cliente trabajas?</DialogTitle>
            <DialogDescription>
              Esta tarea no tiene cliente asignado. Elige uno para que tu tiempo se impute correctamente.
            </DialogDescription>
          </DialogHeader>
          <Select value={clientPickValue} onValueChange={setClientPickValue}>
            <SelectTrigger data-testid="client-pick-select"><SelectValue placeholder="Selecciona un cliente..." /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setClientPickTask(null)} data-testid="client-pick-cancel">Cancelar</Button>
            <Button onClick={confirmClientPick} disabled={!clientPickValue} data-testid="client-pick-confirm">Asignar y arrancar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
